import lodash, { get } from 'lodash';
import Fuse from 'fuse.js/dist/fuse';
import moment from 'moment';
import uuid from 'node-uuid';

import { addApplicationMessage } from 'common/actions/applicationMessagesActions';
import { mutateGraphQL, queryGraphQL } from 'common/util/query';
import { node, nodeWithArgs, toMutation, toQuery } from 'common/util/queryHelper';
import { userDefaultInstitutionIdSelector } from 'common/selectors';
import { loadDataSourceItems } from 'common/actions/dataSources';
import { fuzzyMatchOptions } from 'common/util/fuse/fuse';
import { doaFeatureAccess } from 'common/features/predicates';
import { convertToISOString } from 'common/util/dateTime';

import { formatStudyTeamMembers } from 'pages/study/actions/editStudy';
import { browserHistory } from 'react-router';
import { jsonHelper, QueryNodeRegistry } from '@complion/query';
import { delegationsList, PRINCIPAL_INVESTIGATOR_ROLE } from 'pages/delegationOfAuthority/selectors';
import { buildAddStudyDelegationV2Query } from 'pages/delegationOfAuthority/v2/actions/studyDelegationsV2';
import { delegationsUnderPITenurePredicate } from 'pages/delegationOfAuthority/v2/util/predicates';
import { getPredicateGroups } from 'binder/util/PredicateGroupingVisitor';
import { STUDY_USER_TYPE } from 'components/common/Study/StudyTeamMemberForm';

import { formatMetadataValue } from './editStudy';

export const LOAD_INSTITUTION_STUDY_TYPES = 'LOAD_INSTITUTION_STUDY_TYPES';
export const LOAD_INSTITUTION_USABLE_STUDY_TYPES = 'LOAD_INSTITUTION_USABLE_STUDY_TYPES';
export const LOAD_INSTITUTION_STUDY_TEAM_MEMBERS = 'LOAD_INSTITUTION_STUDY_TEAM_MEMBERS';
export const LOAD_INSTITUTION_STUDY_ROLES = 'LOAD_INSTITUTION_STUDY_ROLES';
export const LOAD_STUDY_METADATA = 'LOAD_STUDY_METADATA';
export const LOAD_STUDY_STAGES = 'LOAD_STUDY_STAGES';
export const SAVE_STUDY_DATA = 'SAVE_STUDY_DATA';
export const CLEAR_STUDY = 'CLEAR_STUDY';
export const LOADING_STUDY_STATE = 'LOADING_STUDY_STATE';
export const STUDY_STATE_LOADED = 'STUDY_STATE_LOADED';
export const SET_STUDY_TEAM_POTENTIAL_MATCH = 'SET_STUDY_TEAM_POTENTIAL_MATCH';
export const SAVING_STUDY_STATE = 'SAVING_STUDY_STATE';
export const STUDY_STATE_SAVED = 'STUDY_STATE_SAVED';

const flattenMetaDataInput = ({ studyInputWithValues, type, institutionId }) => {
    const metadata = lodash.map(studyInputWithValues, ({ name, value, inputFieldType, dataType, descriptorId }) => {

        if (!Array.isArray(value)) {
            value = [value];
        }

        return value.map(userInput => {
            const options = {
                name,
                value: formatMetadataValue(inputFieldType, dataType, userInput),
                inputFieldType,
                dataType,
                institutionId,
                descriptorId
            };

            if (type) options.type = type;

            return options;
        });
    });

    return lodash.flatten(metadata);
};

const metadataFields = [
    'id',
    'name',
    'value',
    'inputFieldType',
    'type',
    'dataType',
    'studyId',
    'institutionId',
    'descriptorId',
];

const studyMetadata = ({ studyInput, type, institutionId }) => {
    const studyInputWithValues = lodash.filter(
        studyInput,
        metadata => !lodash.isEmpty(metadata.value) || !lodash.isNil(metadata.value));

    if (!studyInputWithValues) {
        return false;
    }

    return flattenMetaDataInput({ studyInputWithValues, type, institutionId });
};

const essentialsStudyMetaData = ({ state, institutionId }) => {
    const studyInput = lodash.get(state, 'study.fields.studyEssentials');
    return studyMetadata({ studyInput, type: 'essentials', institutionId });
};

const detailsStudyMetaData = ({ state, institutionId }) => {
    const studyInput = lodash.get(state, 'study.fields.studyDetails');
    return studyMetadata({ studyInput, type: 'binder', institutionId });
};

/**
 * Return required fields and information for study team members
 */
const studyTeamMembers = ({ state, institutionId, studyId }) => {
    const studyTeam = lodash.get(state, 'study.fields.studyTeam', []);

    if (lodash.isEmpty(studyTeam)) {
        return false;
    }

    const newStudyTeams = studyTeam.map(user => {
        return {
            studyId,
            institutionId,
            userId: user.userId,
            studyRoleId: user.studyRoleId,
            nameOfResearcher: user.nameOfResearcher,
            isClinicalStaff: false,
            studyUserType: STUDY_USER_TYPE.TEAM
        };
    });

    const studyTeamMembersFields = [
        'institutionId',
        'nameOfResearcher',
        'userId',
        'studyId',
        'isClinicalStaff',
        'studyUserType'
    ];

    return {
        fields: studyTeamMembersFields,
        input: newStudyTeams
    };
};

const studyDelegations = ({ institutionId, studyId, users, state }) => {
    const isDoaEnabled = doaFeatureAccess(state, institutionId);
    if (!isDoaEnabled && lodash.isEmpty(users)) {
        return;
    }

    const principalInvestigatorRole = lodash.get(state, 'study.studyRoles', []).find(role => {
        return role.name === PRINCIPAL_INVESTIGATOR_ROLE;
    });

    const multiplePrincipalInvestigatorSearch = () => {
        const count = users.filter(user => {
            return user.studyRoleId === lodash.get(principalInvestigatorRole, 'id', null);
        });

        return !!(count.length > 1);
    };

    const multiplePrincipalInvestigators = multiplePrincipalInvestigatorSearch();

    const delegations = [];

    for (const user of users) {
        const signatures = [];
        if (user.studyRoleName === PRINCIPAL_INVESTIGATOR_ROLE) {
            const currentDOA = delegationsList(state);
            const staffDelegationsOverlappingWithNewPITenure = delegationsUnderPITenurePredicate(currentDOA, [
                new Date(),
                null,
            ]);

            const signaturesForNewPI = lodash.map(staffDelegationsOverlappingWithNewPITenure, (delegation) => ({
                delegationId: delegation.id,
                user: {
                    id: user.id,
                    name: user.name,
                },
                signatureType: 'PI_START',
            }));

            signatures.push(...signaturesForNewPI);
        }

        const startDateOrNullIfMultiplePI = () => {
            const startDate = convertToISOString(parseDateFormat(moment().format('LL')));

            if (user.studyRoleId !== lodash.get(principalInvestigatorRole, 'id', null)) {
                return startDate;
            }

            if (!multiplePrincipalInvestigators) {
                return startDate;
            }

            return null;
        };

        delegations.push({
            delegation: {
                id: uuid.v4(),
                studyId,
                startDate: startDateOrNullIfMultiplePI(),
                user: {
                    id: user.id,
                    name: user.name
                },
                roleManagementId: user.studyRoleId,
                auditOrigin: 'Study Settings',
                auditActivity: 'A corresponding delegation is created when a Study Team Member is added in Study Settings.'

            },
            signatureExpectations: signatures,
            defaultSignatureExpectations: signatures,
        });
    }

    return {
        input: delegations,
        fields: [
            'id',
            node('user', ['id', 'displayName', 'emailAddress']),
            node('rolesManagement', ['id', 'name']),
            'taskIds',
            'startDate',
            'endDate',
        ]
    };
};

function isEmptyDetails(state) {
    const details = get(state, 'study.fields.studyDetails', []);

    if (details.length === 0) {
        return true;
    }
    return details.some(detail => !detail.value);
}

const createStudyMutation = async ({ state, studyId, isDraft }) => {
    const institutionId = userDefaultInstitutionIdSelector(state);
    const name = lodash.get(state, 'study.fields.studyName');
    const studyTypeId = lodash.get(state, 'study.fields.studyType.id');
    const currentStageId = lodash.get(state, 'study.fields.studyStage.id');
    const eventsTabEnabled = lodash.get(state, 'study.fields.eventsTabEnabled', true);
    const delegationOfAuthorityEnabled = lodash.get(state, 'study.fields.delegationOfAuthority', true);
    const binderTemplateId = lodash.get(state, 'study.binderTemplate.id');
    const studyTeam = lodash.get(state, 'study.fields.studyTeam', []);
    const billable = lodash.get(state, 'study.fields.billable') || false;
    const isMissingDetail = isEmptyDetails(state);
    const teamMembers = studyTeam.map(member => ({
        id: member.userId, name: member.nameOfResearcher, studyRoleId: member.studyRoleId
    }));
    if (!studyTeam.length) {
        const currentUser = lodash.get(state, 'me', {});

        const studyCreator = { sub: currentUser.sub, userId: currentUser.id, userType: STUDY_USER_TYPE.ADMIN };
        const staffDescriptorDataSources = lodash.get(state, 'study.staffDescriptor.dataSources', null);

        if (staffDescriptorDataSources) {
            const datasource = staffDescriptorDataSources.find((d) => d.value.toLowerCase() === currentUser.name.toLowerCase());
            studyCreator.nameOfResearcher = datasource && datasource.value || currentUser.name;

        }

        studyTeam.push(studyCreator);
    }
    const studyTeamMembersToAdd = formatStudyTeamMembers(studyTeam, institutionId, studyId);
    const studyTeamMembersInputAndFields = studyTeamMembers({ state, institutionId, studyId });
    const binderStudyEssentialDetailsMetaData = essentialsStudyMetaData({ state, institutionId });
    const binderStudySetupMetaData = detailsStudyMetaData({ state, institutionId });
    const studyDelegationsInputAndFields = studyDelegations({ institutionId, studyId, users: teamMembers, state });

    let input = {
        id: studyId,
        name,
        institutionId,
        studyTypeId,
        binderTemplateId,
        currentStageId,
        eventsTabEnabled,
        delegationOfAuthorityEnabled,
    };

    let fields = [
        'id',
        'institutionId',
        'name',
        'studyTypeId',
        'currentStageId'
    ];

    if (studyDelegationsInputAndFields) {
        input.delegations = studyDelegationsInputAndFields.input;
    }

    if (!lodash.isEmpty(studyTeamMembersToAdd)) { //|| !lodash.isEmpty(studyTeamMembersToRemove) || !lodash.isEmpty(studyTeamMembersToUpdate)) {
        // const updatedStudyTeamMembersToRemove = studyTeamMembersToRemove.map(member => {
        //     const newMember = lodash.clone(member);
        //     delete newMember['studyRole'];
        //     delete newMember['user'];
        //     return newMember;
        // });

        input.studyTeamMembersToAdd = studyTeamMembersToAdd;
        // input.studyTeamMembersToRemove = updatedStudyTeamMembersToRemove;
        // input.studyTeamMembersToUpdate = studyTeamMembersToUpdate;

        let studyTeamFieldName = 'studyTeam';
        if (!lodash.isEmpty(studyId)) {
            studyTeamFieldName = 'updateStudyTeam';
        }

        fields = [
            ...fields,
            'binderPermissions',
            node(studyTeamFieldName, ['id'])
        ];
    }

    if (studyTeamMembersInputAndFields) {
        fields = [
            ...fields,
            node('studyTeam', studyTeamMembersInputAndFields.fields),
        ];
    }

    if (binderStudyEssentialDetailsMetaData || binderStudySetupMetaData) {

        if (binderStudyEssentialDetailsMetaData) {
            input.binderStudyEssentialDetailsMetaData = binderStudyEssentialDetailsMetaData;
        }

        if (binderStudySetupMetaData) {
            input.binderStudySetupMetaData = binderStudySetupMetaData;
        }

        fields = [
            ...fields,
            node('metadata', metadataFields)
        ];
    }

    /**
     * Mutation should manage current study state, ex. if study is Draft or Active
     * and update/create accordingly.
     */
    const studyMutation = nodeWithArgs('createStudyV2', {
        institutionId,
        isDraft,
        billable,
        isMissingDetail,
        input
    }, fields);

    return mutateGraphQL(toMutation(studyMutation));
};

const parseDateFormat = (date) => {
    if (lodash.isNil(date)) {
        return null;
    }

    return moment.utc(date);
};

const getStudyEssentialsByStudyTypeId = (institutionId, studyTypeId) => {
    const query = nodeWithArgs('studyDescriptors', {
        institutionId,
        studyTypeId
    }, [
        'id',
        'studyTypeId',
        'name',
        'descriptorId',
        'institutionId',
        'dataSourceId',
        node('dataSources', [
            'value',
            'text',
            'dataType',
            'order'
        ]),
        'inputFieldType',
        'dataType',
        'type'
    ]);

    return queryGraphQL(toQuery(query));
};

function validateResolvedMutation(output, category, dispatch) {
    if (output && output.errors) {
        dispatch(addApplicationMessage('error', `An error occurred when saving study ${category} to the study.`));
    }
}

export function saveStudy(status, nextLocation) {
    return async (dispatch, getState) => {
        const storeState = getState();

        const originalStudyId = lodash.get(storeState, 'study.originalFields.studyId', null);

        let studyId = originalStudyId;
        dispatch(savingStudyStateAction());

        const isDraft = status === 'draft' ? true : false;
        const studyResult = await createStudyMutation({ state: storeState, studyId, isDraft });
        validateResolvedMutation(studyResult, 'type and name', dispatch);
        studyId = lodash.get(studyResult, 'data.createStudyV2.id', null);

        if (isDraft) {
            dispatch(addApplicationMessage('success', 'Changes made to draft study saved.'));
            browserHistory.push(nextLocation || '/studies');
            return;
        }

        dispatch(studyStateSavedAction());
        dispatch(addApplicationMessage('success', 'Study activated.'));
        dispatch({ type: SAVE_STUDY_DATA });
        return studyId;
    };
}

export function getStudyTypesByInstitution(institutionId) {
    return (dispatch) => {
        const query = nodeWithArgs('studyTypes', {
            institutionId,
        }, [
            'id',
            'name',
            'active',
            'category',
            'binderTemplateId'
        ]);

        return queryGraphQL(toQuery(query)).then(results => {
            if (results && results.errors) {
                dispatch(addApplicationMessage('error', 'Failed to fetch study types'));
            } else {
                dispatch({
                    type: LOAD_INSTITUTION_STUDY_TYPES,
                    payload: results.data.studyTypes,
                });
            }
        });
    };
}

export function getUsableStudyTypesByInstitution(institutionId) {
    return (dispatch) => {
        const query = nodeWithArgs('usableStudyTypes', {
            institutionId,
        }, [
            'id',
            'name',
            'active',
            'category',
            'binderTemplateStatus',
            'binderTemplateId'
        ]);

        return queryGraphQL(toQuery(query)).then(results => {
            if (results && results.errors) {
                dispatch(addApplicationMessage('error', 'Failed to fetch study types'));
            } else {
                dispatch({
                    type: LOAD_INSTITUTION_USABLE_STUDY_TYPES,
                    payload: results.data.usableStudyTypes,
                });
            }
        });
    };
}

export function getStudyRolesByInstitution(institutionId) {
    return async dispatch => {
        const query = nodeWithArgs('rolesManagementForInstitution', {
            institutionId,
        }, [
            'id',
            'name',
            'isSystem',
            'active',
        ]);

        const response = await queryGraphQL(toQuery(query));
        if (response && response.errors) {
            dispatch(addApplicationMessage('error', 'Failed to fetch study roles.'));
        } else {
            const studyRoles = response.data.rolesManagementForInstitution;
            const nonSystemRoles = lodash
                .filter(studyRoles, role => role.isSystem === false)
                .sort((role1, role2) => lodash.get(role1, 'name', '').localeCompare(lodash.get(role2, 'name', '')));
            const systemRoles = lodash
                .filter(studyRoles, role => role.isSystem === true)
                .sort((role1, role2) => lodash.get(role1, 'name', '').localeCompare(lodash.get(role2, 'name', '')));
            const newStudyRoles = lodash.concat(systemRoles, nonSystemRoles);

            dispatch({
                type: LOAD_INSTITUTION_STUDY_ROLES,
                payload: newStudyRoles,
            });
        }
    };
}

export function getTeamMembersByInstitution(institutionId) {
    return async dispatch => {

        const query = nodeWithArgs('createStudyGetUsers', {
            institutionId,
            sortField: 'displayName',
            filterField: true,
        }, [
            'id',
            'sub',
            'displayName',
        ]);

        const results = await queryGraphQL(toQuery(query));
        if (results && results.errors) {
            dispatch(addApplicationMessage('error', 'Failed to fetch study team members'));
        } else {
            dispatch({
                type: LOAD_INSTITUTION_STUDY_TEAM_MEMBERS,
                payload: results.data.createStudyGetUsers
            });
        }
    };
}

export function setPotentialResearchersToStudyTeam(studyTeamMembers) {
    return async (dispatch, getState) => {
        const state = getState();
        const staffDescriptorDataSources = lodash.get(state, 'study.staffDescriptor.dataSources', null);

        if (!staffDescriptorDataSources) {
            return;
        }

        const fuzzyMatch = new Fuse(staffDescriptorDataSources, fuzzyMatchOptions);
        const updatedTeamMembers = studyTeamMembers.map((teamMember) => {
            const potentialMatch = fuzzyMatch.search(teamMember.displayName);
            lodash.set(teamMember, 'potentialMatch', potentialMatch);

            return teamMember;
        });

        dispatch({
            type: SET_STUDY_TEAM_POTENTIAL_MATCH,
            payload: updatedTeamMembers,
        });
    };
}

export const createDelegationsV2 = async ({ institutionId, binderId, studyId, users, state, dispatch }) => {
    const isDoaEnabled = doaFeatureAccess(state, institutionId);

    if (isDoaEnabled && !lodash.isEmpty(users)) {
        const delegations = [];
        for (const user of users) {
            const signatures = [];
            if (user.studyRoleName === PRINCIPAL_INVESTIGATOR_ROLE) {
                const currentDOA = delegationsList(state);
                const staffDelegationsOverlappingWithNewPITenure = delegationsUnderPITenurePredicate(currentDOA, [
                    new Date(),
                    null,
                ]);

                const signaturesForNewPI = lodash.map(staffDelegationsOverlappingWithNewPITenure, (delegation) => ({
                    delegationId: delegation.id,
                    user: {
                        id: user.id,
                        name: user.name,
                    },
                    signatureType: 'PI_START',
                }));

                signatures.push(...signaturesForNewPI);
            }
            const startDate = parseDateFormat(moment().format('LL'));

            delegations.push({
                delegation: {
                    id: uuid.v4(),
                    binderId,
                    studyId,
                    startDate: convertToISOString(startDate),
                    user: {
                        id: user.id,
                        name: user.name
                    },
                    roleManagementId: user.studyRoleId,
                    auditOrigin: 'Study Settings',
                    auditActivity: 'A corresponding delegation is created when a Study Team Member is added in Study Settings.'

                },
                signatureExpectations: signatures,
                defaultSignatureExpectations: signatures,
            });
        }

        const query = buildAddStudyDelegationV2Query({ delegations, institutionId, studyId });
        const response = await mutateGraphQL(toMutation(query));
        validateResolvedMutation(response, 'createDelegationsV2', dispatch);
        return response.data.createStudyDelegationV2;
    }
    return [];
};

export function getStudyMetadata(institutionId, binderTemplateId, studyTypeId) {
    return async dispatch => {

        dispatch(loadingStudyStateAction());

        const results = await getBinderTemplateRequirements(institutionId, binderTemplateId);

        if (results && results.errors || binderTemplate === null) {
            return dispatch(addApplicationMessage('error', 'Failed to load all study details'));
        }

        const binderTemplate = results.data.me.binderTemplate;
        const descriptors = results.data.descriptors;
        const deserializedObj = jsonHelper.parse(binderTemplate.rootQuery);

        binderTemplate.rootQuery = QueryNodeRegistry.reconstituteDeserializedJson(deserializedObj);
        binderTemplate.predicateGroups = await getPredicateGroups(binderTemplate.rootQuery);

        const dataSourceIds = getDataSourceIdsFromBinderTemplate(binderTemplate);

        dispatch(loadDataSourceItems(dataSourceIds));

        const studyEssentialResults = await getStudyEssentialsByStudyTypeId(institutionId, studyTypeId);

        if (studyEssentialResults && studyEssentialResults.errors) {
            return dispatch(addApplicationMessage('error', 'Failed to fetch study essentials'));
        }

        const studyEssentials = studyEssentialResults.data.studyDescriptors;

        dispatch({
            type: LOAD_STUDY_METADATA,
            payload: {
                institutionId,
                binderTemplate,
                descriptors,
                studyEssentials
            },
        });

        dispatch(studyStateLoadedAction());
    };
}

export function getBinderTemplateRequirements(institutionId, binderTemplateId) {
    const binderTemplateQuery = node('me', [
        nodeWithArgs('binderTemplate', {
            id: binderTemplateId,
        }, [
            'id',
            'displayName',
            node('rootQuery')
        ])
    ]);

    const descriptorQuery = nodeWithArgs('descriptors', {
        institutionId
    }, [
        'id',
        'displayName',
        'dataType',
        'label',
        'instructions',
    ]);
    return queryGraphQL(toQuery([binderTemplateQuery, descriptorQuery]));
}

export function getStudyStages() {
    return async (dispatch, getState) => {
        const storeState = getState();
        const institutionId = lodash.get(storeState, 'me.institution.id');
        const studyStagesQuery = nodeWithArgs('getStudyStages', {
            institutionId
        }, [
            'id',
            'name',
            'description',
            'institutionId',
            'createdAt',
            'updatedAt',
        ]);

        const response = await queryGraphQL(toQuery(studyStagesQuery));
        if (response.errors) {
            return dispatch(addApplicationMessage('error', 'Failed to load study stages. Please try again'));
        }
        else {
            return dispatch({
                type: LOAD_STUDY_STAGES,
                payload: response.data.getStudyStages
            });
        }
    };
}

export function clearStudy() {
    return dispatch => {
        dispatch({
            type: CLEAR_STUDY,
        });
    };
}

export function discardStudy(message) {
    return dispatch => {
        dispatch(addApplicationMessage('success', message));
        browserHistory.push('/');
    };
}

export function loadingStudyStateAction() {
    return {
        type: LOADING_STUDY_STATE,
    };
}

export function studyStateLoadedAction() {
    return {
        type: STUDY_STATE_LOADED,
    };
}

export function savingStudyStateAction() {
    return {
        type: SAVING_STUDY_STATE,
    };
}

export function studyStateSavedAction() {
    return {
        type: STUDY_STATE_SAVED
    };
}

export function getDataSourceIdsFromBinderTemplate(binderTemplate) {
    return lodash(Array.from(binderTemplate.predicateGroups.values()))
        .map(pg => pg.key.value.dataSourceId)
        .compact()
        .value();
}
